import { graphql } from "gql.tada";

export const RewardFragment = graphql(`
  fragment RewardFragment on Reward @_unmask {
    id
    name
    selection
    description
    descriptionDetails
    finish
    redemptionDetails
    earnedByCurrentParticipantOn
    termsConditions
    maxPerUser
    currentParticipantClaimsCount
    image {
      publicUrl
      publicOptimizedUrl
      altText
    }
    bonusPoints
    participantSweepstakesEntry
    redemptionPoints
    sweepstakeProgress
    sweepstakeEntry
    claimProgress
    showInventory
    claims
    limit
  }
`);

export const ActiveRewardsQuery = graphql(`
  query GetActiveRewards(
    $featured: Boolean
    $tagSlugs: [String!]
    $tagContain: Boolean
    $sort: RewardsSortEnum
  ) {
    activeRewards(
      featured: $featured
      tagSlugs: $tagSlugs
      tagContain: $tagContain
      sort: $sort
    ) {
      id
      name
      description
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      finish
      bonusPoints
      visibility
      selection
      status
      feature
      earnedByCurrentParticipantOn
      participantSweepstakesEntry
      redemptionPoints
      claimProgress
      sweepstakeProgress
      sweepstakeEntry
      showInventory
      claims
      limit
      tags {
        name
      }
    }
    claimedRewards {
      id
      name
      description
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      finish
      bonusPoints
      visibility
      selection
      status
      feature
      finish
    }
  }
`);

export const RewardQuery = graphql(
  `
    query GetRewardDetails($id: String!) {
      reward(id: $id) {
        ...RewardFragment
      }
    }
  `,
  [RewardFragment]
);

export const RewardsExplorerQuery = graphql(`
  query GetRewardsExplorer(
    $contentTagSlugs: [String!]
    $sort: RewardsSortEnum
    $contentTagSource: String
  ) {
    activeRewards(contentTagSlugs: $contentTagSlugs, sort: $sort) {
      id
      name
      description
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      claims
      limit
      showInventory
      finish
      bonusPoints
      visibility
      selection
      status
      feature
      earnedByCurrentParticipantOn
      participantSweepstakesEntry
      redemptionPoints
      claimProgress
      sweepstakeProgress
      sweepstakeEntry
      contentTags {
        name
      }
    }
    me {
      earnedRewardsDetails(contentTagSlugs: $contentTagSlugs) {
        id
        name
        description
        image {
          publicUrl
          publicOptimizedUrl
          altText
        }
        finish
        bonusPoints
        visibility
        selection
        status
        feature
         limit
        claims
        showInventory
        earnedByCurrentParticipantOn
        participantSweepstakesEntry
        redemptionPoints
        claimProgress
        sweepstakeProgress
        sweepstakeEntry
        contentTags {
          name
        }
      }
    }
    contentTags(source: $contentTagSource) {
      name
      slug
    }
  }
`);

export const FeaturedRewardsQuery = graphql(`
  query GetFeaturedRewards {
    activeRewards(featured: true) {
      id
      name
      description
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      limit
      claims
      showInventory
      finish
      bonusPoints
      visibility
      selection
      status
      feature
      earnedByCurrentParticipantOn
      participantSweepstakesEntry
      redemptionPoints
      claimProgress
      sweepstakeProgress
      sweepstakeEntry
      contentTags {
        name
      }
    }
  }
`);

export const BadgeRewardsQuery = graphql(`
  query GetBadgeRewards {
    badgeRewards {
      id
      name
      description
      finish
      bonusPoints
      status
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      feature
      earnedByCurrentParticipantOn
      redemptionDetails
      redemptionPoints
      redemptionDetails
      descriptionDetails
      termsConditions
      claimProgress
      achievementReward {
        activeImage {
          publicUrl
          publicOptimizedUrl
          altText
        }
        howToEarn
      }
    }
  }
`);

export const DonationRewardsQuery = graphql(`
  query GetDonationRewards {
    donationRewards {
      id
      name
      description
      finish
      bonusPoints
      status
      redemptionPoints
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      feature
      donationReward {
        hasGoal
        goal
        goalProgress
        hasMetrics
        metric
        metricProgress
        metricText
        metricConversionRate
      }
    }
  }
`);

export const DonationRewardDetailsQuery = graphql(`
  query GetDonationReward($id: String!) {
    donationReward(id: $id) {
      id
      name
      description
      finish
      bonusPoints
      status
      redemptionPoints
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      redemptionDetails
      feature
      donationReward {
        hasGoal
        goal
        goalProgress
        hasMetrics
        metric
        metricProgress
        metricText
        metricConversionRate
        photos {
          publicUrl
          publicOptimizedUrl
          altText
        }
        optionSet {
          id
          required
          caption
          displayType
          dependsOn
          options {
            id
            title
            description
            sequence
            image {
              publicUrl
              publicOptimizedUrl
              altText
            }
            metadata
            optionAmountSubmissions
            childOptionIds
          }
        }
      }
    }
  }
`);

export const TokenRewardDetailsQuery = graphql(`
  query GetTokenRewardDetails($id: String!) {
    reward(id: $id) {
      id
      name
      description
      descriptionDetails
      finish
      bonusPoints
      status
      redemptionPoints
      requiresShipping
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      redemptionDetails
      termsConditions
      feature
      claimProgress
      maxPerUser
      tokenReward {
        tokenProgress
        earnedToken
        claimsCount
        fromFutureTier
        tier
        nextReward {
          name
          redemptionPoints
        }
        optionSet {
          id
          required
          caption
          displayType
          dependsOn
          options {
            title
            description
            image {
              publicUrl
              publicOptimizedUrl
              altText
            }
            id
            childOptionIds
          }
        }
      }
    }
  }
`);

export const TokenRewardsExplorerQuery = graphql(`
  query GetTokenRewardsExplorer($contentTagSlugs: [String!]) {
    tokenRewards(contentTagSlugs: $contentTagSlugs) {
      id
      name
      description
      image {
        publicUrl
        publicOptimizedUrl
        altText
      }
      finish
      bonusPoints
      visibility
      selection
      status
      feature
      earnedByCurrentParticipantOn
      redemptionPoints
      claimProgress
      maxPerUser
      contentTags {
        name
      }
      tokenReward {
        tokenProgress
        earnedToken
        claimsCount
        fromFutureTier
      }
    }
  }
`);
